// ============================================================================
// APPLY ON-SAVE-ROLLED TRIGGERS
// ============================================================================
// [CREATED: 2026-05-17 — Spec C Task 5]
// [PURPOSE]: Wraps `effectCalculator.fireTriggeredEffects('on_save_rolled', ...)`
//   and applies payload deltas relevant to the in-flight save roll:
//
//     - `advantage` grant       → mode flip ('normal' → 'advantage';
//                                  'disadvantage' → 'normal'; 'advantage'
//                                  stays put).
//     - any other payload type  → recorded in triggeredLogEntries so the
//                                  activity log surfaces them
//                                  (Rule 25: never silently inert).
//
//   onceThenConsume firings carry their sourceRef into pendingConsumes for
//   the caller to plumb through consumeTriggeredSource (Task 4).
//
// [WHY]: The save-roll call sites (QuickActionsBar.rollSavingThrow, DiceTab
//   save buttons) need a shared, unit-testable hook. Inlining the firer +
//   payload walk at each site would be a Rule 22 violation (the test couldn't
//   exercise the real production wiring without driving the whole component).
//
// [SPEC]: docs/superpowers/specs/2026-05-16-triggered-effect-design.md §5/§9
// [PLAN]: docs/superpowers/plans/2026-05-16-triggered-effect.md Task 5

import { effectCalculator } from '../services/effectCalculator';

/**
 * Mode-flip semantics:
 *   normal + advantage      → advantage
 *   disadvantage + advantage → normal (they cancel; matches existing pattern
 *                               at QuickActionsBar.js:184-188 for condition-
 *                               based disadvantage canceling advantage)
 *   advantage + advantage   → advantage (idempotent)
 */
function _flipModeForAdvantage(currentMode) {
  if (currentMode === 'disadvantage') return 'normal';
  return 'advantage';
}

/**
 * @param {Object} playerStats
 * @param {Array}  inventory
 * @param {Object} eventContext   { ability, mode, when?, dc? }
 * @returns {{ finalMode: string, triggeredLogEntries: Array, pendingConsumes: Array }}
 */
export default function applyOnSaveRolledTriggers(playerStats, inventory, eventContext) {
  const ctx = eventContext && typeof eventContext === 'object' ? eventContext : {};
  const startingMode = ctx.mode || 'normal';
  const result = {
    finalMode: startingMode,
    triggeredLogEntries: [],
    pendingConsumes: [],
  };

  // Empty eventContext → no ability filter would match a registered grant; bail.
  if (!ctx.ability) return result;

  const firings = effectCalculator.fireTriggeredEffects(
    'on_save_rolled',
    playerStats,
    inventory || [],
    ctx
  );
  if (!Array.isArray(firings) || firings.length === 0) return result;

  for (const firing of firings) {
    if (!firing || typeof firing !== 'object') continue;
    const sourceLabel = firing.sourceLabel || 'Triggered Effect';
    const payload = Array.isArray(firing.payload) ? firing.payload : [];

    for (const entry of payload) {
      if (!entry || typeof entry !== 'object') continue;

      if (entry.type === 'advantage' && entry.operation === 'grant') {
        result.finalMode = _flipModeForAdvantage(result.finalMode);
        result.triggeredLogEntries.push({
          sourceLabel,
          kind: 'advantage',
          target: entry.target || null,
        });
        continue;
      }

      // Non-advantage payload — log so the surface isn't silently inert.
      result.triggeredLogEntries.push({
        sourceLabel,
        kind: 'other',
        payloadType: entry.type,
        operation: entry.operation || null,
        value: entry.value ?? null,
      });
    }

    if (firing.shouldConsume && firing.sourceRef && typeof firing.sourceRef === 'object') {
      result.pendingConsumes.push(firing.sourceRef);
    }
  }

  return result;
}
